import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Toast types
 */
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

/**
 * Toast positions
 */
export const TOAST_POSITIONS = {
  TOP_RIGHT: 'top-right',
  TOP_CENTER: 'top-center',
  BOTTOM_RIGHT: 'bottom-right',
  BOTTOM_CENTER: 'bottom-center',
};

/**
 * Default durations by type (in milliseconds)
 */
const DEFAULT_DURATIONS = {
  [TOAST_TYPES.SUCCESS]: 5000,
  [TOAST_TYPES.ERROR]: 0, // Never auto-dismiss errors
  [TOAST_TYPES.WARNING]: 7000,
  [TOAST_TYPES.INFO]: 5000,
};

/**
 * Toast Context
 */
const ToastContext = createContext();

/**
 * Hook to use toasts
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

/**
 * Toast Provider Component
 *
 * Provides centralized toast notification management across the entire application.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {number} props.maxToasts - Maximum number of toasts to show at once
 * @param {string} props.defaultPosition - Default toast position
 */
export const ToastProvider = ({
  children,
  maxToasts = 5,
  defaultPosition = TOAST_POSITIONS.TOP_RIGHT,
}) => {
  const [toasts, setToasts] = useState([]);

  /**
   * Generate unique ID for toasts
   */
  const generateId = useCallback(() => {
    return `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  /**
   * Remove a toast
   */
  const removeToast = useCallback((id) => {
    setToasts((prev) =>
      prev.map((toast) =>
        toast.id === id ? { ...toast, exiting: true } : toast,
      ),
    );

    // Remove from DOM after animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 300);
  }, []);

  /**
   * Add a new toast
   */
  const addToast = useCallback(
    (toast) => {
      const newToast = {
        id: generateId(),
        timestamp: new Date(),
        position: defaultPosition,
        dismissible: true,
        ...toast,
        duration:
          toast.duration !== undefined
            ? toast.duration
            : DEFAULT_DURATIONS[toast.type] || 5000,
      };

      let addedToastId = newToast.id;

      setToasts((prev) => {
        // Prevent duplicate toasts (same type + message)
        const duplicate = prev.find(
          (t) =>
            t.type === toast.type && t.message === toast.message && !t.exiting,
        );

        if (duplicate) {
          addedToastId = duplicate.id;
          return prev; // Don't add, return existing state
        }

        // Add new toast and keep only max toasts
        const updated = [...prev, newToast];
        return updated.slice(-maxToasts);
      });

      // Only set up auto-dismiss if this is a new toast (not duplicate)
      if (addedToastId === newToast.id && newToast.duration > 0) {
        setTimeout(() => {
          removeToast(newToast.id);
        }, newToast.duration);
      }

      return addedToastId;
    },
    [generateId, maxToasts, defaultPosition, removeToast],
  );

  /**
   * Clear all toasts
   */
  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  /**
   * Convenience methods for different toast types
   */
  const showSuccess = useCallback(
    (message, options = {}) => {
      return addToast({
        type: TOAST_TYPES.SUCCESS,
        title: options.title || 'Success',
        message,
        ...options,
      });
    },
    [addToast],
  );

  const showError = useCallback(
    (message, options = {}) => {
      return addToast({
        type: TOAST_TYPES.ERROR,
        title: options.title || 'Error',
        message,
        duration: 0, // Errors never auto-dismiss
        ...options,
      });
    },
    [addToast],
  );

  const showWarning = useCallback(
    (message, options = {}) => {
      return addToast({
        type: TOAST_TYPES.WARNING,
        title: options.title || 'Warning',
        message,
        ...options,
      });
    },
    [addToast],
  );

  const showInfo = useCallback(
    (message, options = {}) => {
      return addToast({
        type: TOAST_TYPES.INFO,
        title: options.title || 'Info',
        message,
        ...options,
      });
    },
    [addToast],
  );

  const contextValue = {
    // State
    toasts,

    // Actions
    addToast,
    removeToast,
    clearAll,

    // Convenience methods
    showSuccess,
    showError,
    showWarning,
    showInfo,

    // Constants
    TOAST_TYPES,
    TOAST_POSITIONS,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  );
};

export default ToastProvider;
